import {
  Box, Button, CircularProgress, Pagination, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme
} from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import Header from "components/Header";
import { useNavigate } from "react-router";
import loadingStore from "core/utils/loading";
import { observer } from "mobx-react";
import adviserStore from "../api";
import { Adviser } from "../model";
import AdviserUsersRow from "./adviser-users-row";
import { hasPermission, Permission } from "core/utils/roles";
import { useAuth } from "app/authHooks";
import Filter from "./filters";
import { useSelector } from "react-redux";
import { RootState } from "app/store";
import { InputValues } from "core/filter/filterSlice";

const TablePlaceholder: React.FC<{ children: ReactNode }> = ({ children }) => (
  <TableRow>
    <TableCell colSpan={9} align="center">
      {children}
    </TableCell>
  </TableRow>
);

function AdviserUsersTable() {
  const { attributes } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const { getAdvisers } = adviserStore;
  const { loading } = loadingStore;
  const canCreate = hasPermission("create:advisor-onboarding" as Permission);


  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(10);
  const [sortField, setSortField] = useState<keyof Adviser>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [users, setUsers] = useState<Adviser[]>([]);
  const filters = useSelector((state: RootState) => state.filter.advisorFilter || []);
  // const [filters, setFilters] = useState<InputValues[]>();


  useEffect(() => {
    onInit((filters),
      currentPage,
      sortField,
      sortOrder);
  }, [filters, currentPage, sortField, sortOrder]);

  const onInit = async (filters: InputValues[] | null,
    page: number,
    field: keyof Adviser | null,
    order: "asc" | "desc") => {
    try {
      const users = await getAdvisers({
        filters,
        pageIndex: page - 1,
        pageSize,
        sortBy: field,
        sortOrder: order
      });
      setUsers(users?.data || []);
      setTotalPages(Math.ceil((users?.total || 0) / pageSize));
    } catch (error) {
      console.error("Failed to fetch advisers:", error);
    }
  };

  const handleSort = (field: keyof Adviser) => {
    const isAsc = sortField === field && sortOrder === "asc";
    setSortField(field);
    setSortOrder(isAsc ? "desc" : "asc");
  };

  return (
    <Box sx={{ paddingBottom: "64px" }}>
      <Box display="flex" justifyContent="space-between">
        <Header title="Adviser Users" subtitle="Manage Adviser Users" />
        {canCreate && (
          <Box display="flex" flexDirection="row" gap="10px" alignItems="center">
            <Button
              type="submit"
              color="primary"
              variant="contained"
              onClick={() => navigate("/adviser-users/add-edit")}
            >
                  Add Adviser Users
            </Button>
          </Box>
        )}
      </Box>

      <Box py="20px">
        <Filter
          filters = { filters }
        />
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              {[
                { field: "first_name", label: "Name" },
                { field: "username", label: "Username" },
                { field: "email", label: "Email" },
                { field: "customer_id", label: "Customer" },
                { field: "employee_id", label: "Employee Id" }
              ].map(({ field, label }) => (
                <TableCell key={field} onClick={() => handleSort(field as keyof Adviser)}>
                  <Typography noWrap variant="h5">
                    {label}{" "}
                    <span style={{ opacity: 0.75 }}>
                      {sortField === field ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                    </span>
                  </Typography>
                </TableCell>
              ))}
              <TableCell>
                <Typography noWrap variant="h5">
                  Action
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TablePlaceholder>
                <CircularProgress />
              </TablePlaceholder>
            )}
            {!loading && !users?.length && (
              <TablePlaceholder>
                <Typography variant="h5">No Data</Typography>
              </TablePlaceholder>
            )}
            {!loading && users?.map((row, index) => (
              <AdviserUsersRow row={row} key={index} attributes={attributes} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          padding: "10px",
          boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.1)",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Pagination
          className="my-3"
          count={totalPages}
          page={currentPage}
          variant="outlined"
          onChange={(event, value) => setCurrentPage(value)}
        />
      </Box>
    </Box>
  );
}

export default observer(AdviserUsersTable);
